import { getBuildingProtectionStatus } from '@popety-io/utils'

export interface BuildingData {
  id: string
  name: string
  nameIndex?: number
  egid?: string
  address?: string
  type: string
  area?: number
  floors?: number
  constructionYear?: number
  percentage: number
  isProtected?: boolean
  isUnderConstruction?: boolean
  isUnderground?: boolean
  isProjected?: boolean
  status?: string
  volume?: number
  height?: number
  floorArea?: number
  constructionStatus?: string
  protectionStatus?: string
  lastRenovation?: string
  compliance?: string
  officialNumber?: string
  ofsCategory?: string
  classification?: string
  units?: number
  mainHeating?: string
  secondaryHeating?: string
  waterHeating?: string
  solarPotential?: string
  popetyClassification?: string
  isVolumeEstimated?: boolean
  isHeightEstimated?: boolean
  isFloorAreaEstimated?: boolean
  isAreaEstimated?: boolean
}

// Translation marker keys used as value placeholders.
// These are resolved in BuildingDetails.tsx and BuildingCard.tsx
// using the buildingDetails.values.* keys from the land-analysis i18n namespace.
export const BUILDING_VALUES = {
  NOT_PROTECTED: '__NOT_PROTECTED__',
  HERITAGE_PROTECTED: '__HERITAGE_PROTECTED__',
  COMPLIANT: '__COMPLIANT__',
  NOT_CLASSIFIED: '__NOT_CLASSIFIED__',
  NOT_SPECIFIED: '__NOT_SPECIFIED__',
  NA: '__NA__',
  SOLAR_EXCELLENT: '__SOLAR_EXCELLENT__',
  SOLAR_GOOD: '__SOLAR_GOOD__',
  SOLAR_MEDIUM: '__SOLAR_MEDIUM__',
  SOLAR_LOW: '__SOLAR_LOW__',
  PROJECTED: '__PROJECTED__',
  BUILDING_NAME: '__BUILDING_NAME__',
} as const

export const processBuildings = (entity: any): BuildingData[] => {
  const buildings: BuildingData[] = []
  const buildingsArray = entity?.buildings

  if (buildingsArray && buildingsArray.length > 0) {
    buildingsArray.forEach((building: any, index: number) => {
      const admin = building.buildings_administrative?.[0]
      const address = building.addresses?.[0]
      const protectionStatus = getBuildingProtectionStatus(building)

      const resolvedVolume = admin?.volume_estimate || building.volume_estimate
      const isVolumeEstimated = !!resolvedVolume

      const resolvedHeight =
        admin?.max_height || building.height || building.height_estimate
      const isHeightEstimated =
        !admin?.max_height && !building.height && !!building.height_estimate

      const resolvedArea = admin?.area || building.area
      const isAreaEstimated = !admin?.area && !!building.area

      buildings.push({
        id: `building-${index}`,
        name: BUILDING_VALUES.BUILDING_NAME,
        nameIndex: index + 1,
        egid: admin?.egid?.toString(),
        address: address
          ? `${address.street} ${address.street_nb || ''}`.trim()
          : undefined,
        type: getBuildingType(building),
        area: resolvedArea,
        floors: admin?.floor_nb || building.floor_nb || 1,
        constructionYear: admin?.building_year,
        percentage: (building.cover_area_perc || 1) * 100,
        isProtected:
          protectionStatus === 'unknown'
            ? undefined
            : protectionStatus === 'protected',
        isUnderConstruction: admin?.under_construction,
        isUnderground: admin?.underground,
        isProjected:
          building.is_projected === true ||
          building.classification === 'projected',
        status: admin?.status || building.status,
        volume: resolvedVolume,
        height: resolvedHeight,
        floorArea: admin?.area || building.area,
        constructionStatus: admin?.status,
        protectionStatus:
          protectionStatus === 'protected'
            ? BUILDING_VALUES.HERITAGE_PROTECTED
            : protectionStatus === 'not-protected'
              ? BUILDING_VALUES.NOT_PROTECTED
              : undefined,
        lastRenovation: admin?.renovation_year?.toString(),
        compliance: BUILDING_VALUES.COMPLIANT,
        officialNumber: building.official_number?.toString(),
        ofsCategory: getOFSCategory(building),
        classification:
          building?.classification || BUILDING_VALUES.NOT_CLASSIFIED,
        units: admin?.unit_nb || building.unit_nb || 0,
        mainHeating:
          admin?.heating_energy_source || BUILDING_VALUES.NOT_SPECIFIED,
        secondaryHeating: admin?.secondary_heating || '-',
        waterHeating: admin?.water_heating || BUILDING_VALUES.NOT_SPECIFIED,
        solarPotential: getSolarPotentialLabel(building),
        popetyClassification:
          admin?.popety_classification || BUILDING_VALUES.NOT_CLASSIFIED,
        isVolumeEstimated,
        isHeightEstimated,
        isFloorAreaEstimated: true,
        isAreaEstimated,
      })
    })
  }

  return buildings
}

export const getBuildingType = (building: any): string => {
  if (building.building_class) {
    const buildingClass = building.building_class.toLowerCase()

    if (buildingClass.includes('residential minority'))
      return 'residential-minority'
    if (buildingClass.includes('residential majority'))
      return 'residential-majority'
    if (buildingClass.includes('residential')) return 'residential'

    if (
      buildingClass.includes('retail') ||
      buildingClass.includes('commercial')
    )
      return 'retail'
    if (buildingClass.includes('office')) return 'offices'
    if (buildingClass.includes('industrial')) return 'industrial'
    if (buildingClass.includes('public')) return 'public'
    if (buildingClass.includes('garage') || buildingClass.includes('parking'))
      return 'garage'
    if (buildingClass.includes('not classified')) return 'not-classified'
  }

  const admin = building.buildings_administrative?.[0]
  if (admin?.building_type) {
    const type = admin.building_type.toLowerCase()
    if (type.includes('residential minority')) return 'residential-minority'
    if (type.includes('residential majority')) return 'residential-majority'
    if (type.includes('residential')) return 'residential'
    if (type.includes('commercial')) return 'retail'
    if (type.includes('office')) return 'offices'
    if (type.includes('not classified')) return 'not-classified'
  }

  return 'other'
}

export const getOFSCategory = (building: any): string => {
  const admin = building.buildings_administrative?.[0]
  if (admin?.category) return admin.category
  if (building.building_class) return building.building_class
  return BUILDING_VALUES.NOT_CLASSIFIED
}

export const getBuildingIconType = (type: string): string => {
  switch (type) {
    case 'residential':
    case 'residential-minority':
    case 'residential-majority':
    case 'retail':
    case 'offices':
    case 'industrial':
    case 'public':
    case 'garage':
    case 'not-classified':
      return type
    default:
      return 'other'
  }
}

export const getBuildingIcon = (type: string): string => {
  switch (type) {
    case 'residential':
    case 'residential-minority':
    case 'residential-majority':
      return 'residential'
    case 'retail':
      return 'retail'
    case 'offices':
      return 'offices'
    case 'industrial':
      return 'industrial'
    case 'public':
      return 'public'
    case 'garage':
      return 'garage'
    case 'not-classified':
    default:
      return 'building-permit'
  }
}

export const formatArea = (area?: number) => {
  if (!area) return BUILDING_VALUES.NA
  return `${area.toLocaleString()} m²`
}

export const getSolarPotentialLabel = (building: any): string => {
  const efficiency =
    building.solar_electrical_efficiency ||
    building.solar_electrical_efficiency_best
  if (efficiency == null) return '-'
  if (efficiency >= 1000) return BUILDING_VALUES.SOLAR_EXCELLENT
  if (efficiency >= 600) return BUILDING_VALUES.SOLAR_GOOD
  if (efficiency >= 300) return BUILDING_VALUES.SOLAR_MEDIUM
  return BUILDING_VALUES.SOLAR_LOW
}

const EXISTING_STATUSES = ['existant', 'existing']
const NON_USABLE_STATUSES = ['non utilisable']
const PLANNED_STATUSES = [
  'autorisé',
  'en construction',
  'en projet',
  'in_construction',
  'project',
]

export interface ClassifiedBuildings {
  registered: BuildingData[]
  planned: BuildingData[]
  other: BuildingData[]
}

export const classifyBuildings = (
  buildings: BuildingData[]
): ClassifiedBuildings => {
  const registered: BuildingData[] = []
  const planned: BuildingData[] = []
  const other: BuildingData[] = []

  buildings.forEach((b) => {
    if (b.isProjected) {
      if (b.egid && EXISTING_STATUSES.includes(b.status || '')) {
        registered.push(b)
      } else if (b.egid && NON_USABLE_STATUSES.includes(b.status || '')) {
        registered.push(b)
      } else {
        planned.push(b)
      }
    } else if (b.egid) {
      if (
        !b.status ||
        EXISTING_STATUSES.includes(b.status) ||
        NON_USABLE_STATUSES.includes(b.status)
      ) {
        registered.push(b)
      } else if (PLANNED_STATUSES.includes(b.status)) {
        planned.push(b)
      } else {
        registered.push(b)
      }
    } else {
      other.push(b)
    }
  })

  return { registered, planned, other }
}
